import React, { useEffect, useState } from "react";
import { Loader2, LogOut, Inbox } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { authService, formatApiError } from "../../services/catalogService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Task 17 — internal leads table: every contact form submission.
export default function LeadsPanel({ admin, onLogout }) {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${authService.getToken()}` }, timeout: 10000 })
      .then(({ data }) => setLeads(data.leads || []))
      .catch((e) => toast.error("Could not load leads", { description: formatApiError(e) }));
  }, []);

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-leads-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Leads</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Every contact form submission, newest first. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {leads === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : leads.length === 0 ? (
        <div className="border border-black/10 bg-white p-10 text-center text-[#817B74]" data-testid="leads-empty">
          <Inbox size={24} strokeWidth={1.4} className="mx-auto mb-3 text-[#D92A47]" />
          No leads yet — they appear here as the contact form is used.
        </div>
      ) : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#817B74] border-b border-black/10">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Origin</th>
                <th className="px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-black/5 align-top" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-[#817B74]">
                    {l.received_at ? new Date(l.received_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td className="px-4 py-3 font-medium text-[#252525]">{l.company}</td>
                  <td className="px-4 py-3">{l.contact_name}</td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${l.business_email}`} className="text-[#D92A47] hover:underline">{l.business_email}</a>
                  </td>
                  <td className="px-4 py-3">{l.country}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.business_type}</td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <span className="block truncate" title={l.message}>{l.message || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-[#817B74] text-xs" data-testid={`lead-origin-${l.id}`}>
                    {l.origin_page && <span className="block font-medium text-[#252525]">{l.origin_page}</span>}
                    {l.utm_source && <span className="block">utm: {l.utm_source}</span>}
                    {!l.utm_source && l.referrer && (
                      <span className="block truncate max-w-[180px]" title={l.referrer}>{l.referrer}</span>
                    )}
                    {!l.origin_page && !l.utm_source && !l.referrer && "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border ${
                      l.source === "popup" ? "border-[#C4A46B] text-[#8a6b2f]" : "border-black/15 text-[#817B74]"
                    }`}>
                      {l.source === "popup" ? "Popup" : "Form"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
